import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { TimeFrameMode } from 'src/app/shared/interfaces/global-filter-model';
import { MondelezDataService } from 'src/app/shared/services/mondelez-data.service';
import { Subscription } from 'rxjs';
import { Chart, registerables } from 'chart.js';

import { PrecisionType } from 'src/app/shared/interfaces/chart.model';
import { GlobalFilterService } from 'src/app/shared/services/global-filter.service';

Chart.register(...registerables);

@Component({
  selector: 'app-capacity-chart',
  templateUrl: './capacity-chart.component.html',
  styleUrls: ['./capacity-chart.component.scss'],
})
export class CapacityChartComponent implements OnInit, OnChanges, OnDestroy {
  @Input() capacityData: any;
  @Input() demandData: any;
  @Input() capacityWaterfallData: any;
  @Input() showTimeFrameModeToggle = true;

  public chartObj;

  // public capacityData: capacityDataset[];
  public aggregatedData = [];

  private yearRange = {
    startYear: undefined,
    endYear: undefined,
  };
  public timeFrameMode: TimeFrameMode;
  public chartParams = [];
  public chartData = {
    year: {},
    quarter: {},
  };

  private subs = new Subscription();

  constructor(
    private mondelezDataService: MondelezDataService,
    private globalFilterService: GlobalFilterService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (this.timeFrameMode) {
      this.prepareChart();
      this.aggregatedData = this.aggregator();
    }
  }

  onTimeFrameChange(value: TimeFrameMode) {
    this.mondelezDataService.updateTimeFrameMode(value);
  }

  ngOnInit(): void {
    this.subs.add(
      this.globalFilterService.globalFilters.subscribe((filter) => {
        if (filter) {
          this.yearRange.startYear = filter.start_year;
          this.yearRange.endYear = filter.end_year;
        }
      }),
    );
    this.subs.add(
      this.mondelezDataService.timeFrameMode.subscribe((value) => {
        this.timeFrameMode = value;
        this.prepareChart();
        this.aggregatedData = this.aggregator();
      }),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private prepareChart() {
    this.chartParams = [
      { name: 'demand', type: 'bar', label: 'Demand', color: '#C4C4CD', stacked: true, stack: '0', legend: { isTop: true, isRight: false }, precision: PrecisionType.NONE },
      {
        name: 'im_capacity',
        type: 'bar',
        label: 'IM',
        color: '#FBCC79',
        stacked: true,
        stack: '1',
        precision: PrecisionType.NONE,
        dataLabel: { color: '#8A6930', border: '#8A6930', bg: '#FCF8EE' },
      },

      {
        name: 'em_capacity',
        type: 'bar',
        label: 'EM',
        color: '#FAF3E3',
        stacked: true,
        stack: '1',
        precision: PrecisionType.NONE,

        dataLabel: { color: '#8A6930', border: '#8A6930', bg: '#FCF8EE' },
      },
      {
        name: 'incremental_im_capacity',
        type: 'bar',
        label: 'Change in Capacity (IM)',
        color: '#949494',
        stacked: true,
        stack: '1',
        precision: PrecisionType.NONE,

        dataLabel: { color: '#8A6930', border: '#8A6930', bg: '#FCF8EE' },
      },
      {
        name: 'incremental_em_capacity',
        type: 'bar',
        label: 'Change in Capacity (EM)',
        color: '#D0D0D0',
        stacked: true,
        stack: '1',
        precision: PrecisionType.NONE,
        dataLabel: { color: '#8A6930', border: '#8A6930', bg: '#FCF8EE' },
      },
    ];

    this.chartData[this.timeFrameMode]['demand'] = this.calculateVals('demand');
    this.chartData[this.timeFrameMode]['im_capacity'] = this.calculateVals('im_capacity');
    this.chartData[this.timeFrameMode]['em_capacity'] = this.calculateVals('em_capacity');
    this.chartData[this.timeFrameMode]['incremental_im_capacity'] = this.calculateVals('incremental_im_capacity');
    this.chartData[this.timeFrameMode]['incremental_em_capacity'] = this.calculateVals('incremental_em_capacity');
  }

  private calculateVals(type) {
    if (type == 'demand') {
      let dataSliceIndex = 0;
      if (this.demandData[this.timeFrameMode]['total'].length > this.getTimeRangeLable().length) {
        dataSliceIndex = 1;
      }
      if (this.demandData[this.timeFrameMode] != undefined && this.demandData[this.timeFrameMode]['total'] != undefined) {
        return this.demandData[this.timeFrameMode]['total'].slice(dataSliceIndex).map((el) => this.formatNumbers(el));
      } else {
        return [];
      }
    } else {
      if (this.capacityData[this.timeFrameMode] != undefined && this.capacityData[this.timeFrameMode][type] != undefined) {
        return this.capacityData[this.timeFrameMode][type].map((el) => this.formatNumbers(el));
      } else {
        return [];
      }
    }
  }

  getTimeRangeLable() {
    const labels = [];
    for (let year = this.yearRange.startYear; year < this.yearRange.endYear + 1; year++) {
      if (this.timeFrameMode == TimeFrameMode.QUARTERLY) {
        for (let q = 1; q <= 4; q++) {
          labels.push(year + '-Q' + q);
        }
      } else {
        labels.push(year);
      }
    }
    return labels;
  }

  private formatNumbers(amt: number) {
    if (!amt) return 0;
    let millionth = amt / 1000000;
    return parseFloat(millionth.toFixed(1));
  }

  private aggregator() {
    const performance = this.capacityWaterfallData['ge_improvement'] + this.capacityWaterfallData['wastage_improvement'] + this.capacityWaterfallData['shifts_improvement'];
    const new_lines = this.capacityWaterfallData['new_improvement'];
    const em = this.capacityWaterfallData['intervention_capacity'];
    const line_restarts = this.capacityWaterfallData['line_shutdown'];
    const total = performance + new_lines + em + line_restarts;
    return [
      {
        label: 'Performance',
        value: this.formatNumbers(performance),
        prefix: '',
        suffix: `(${isFinite(Math.round((performance / total) * 100 * 10) / 10) ? Math.round((performance / total) * 100 * 10) / 10 : 0}%)`,
      },
      {
        label: 'New Lines',
        value: this.formatNumbers(new_lines),
        prefix: '',
        suffix: `(${isFinite(Math.round((new_lines / total) * 100 * 10) / 10) ? Math.round((new_lines / total) * 100 * 10) / 10 : 0}%)`,
      },
      {
        label: 'Intervention',
        value: this.formatNumbers(em),
        prefix: '',
        suffix: `(${isFinite(Math.round((em / total) * 100 * 10) / 10) ? Math.round((em / total) * 100 * 10) / 10 : 0}%)`,
      },
      {
        label: 'Line Shutdown',
        value: this.formatNumbers(line_restarts),
        prefix: '',
        suffix: `(${isFinite(Math.round((line_restarts / total) * 100 * 10) / 10) ? Math.round((line_restarts / total) * 100 * 10) / 10 : 0}%)`,
      },
    ];
  }
}
